import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, wsUrl } from "../lib/api";
import { useAuth } from "./AuthContext";
import { fireNotification } from "../lib/notifications";

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ unread_emails: 0, unread_messages: 0, pending_validations: 0, unread_tickets: 0 });
  const [perCompany, setPerCompany] = useState({});
  const [events, setEvents] = useState(0); // bump to trigger refetch in pages
  const wsRef = useRef(null);
  const listeners = useRef(new Set());

  const refreshSummary = useCallback(async () => {
    try {
      const [s, pc] = await Promise.all([api.get("/notifications/summary"), api.get("/notifications/by-company")]);
      setSummary(s.data);
      setPerCompany(pc.data || {});
    } catch {}
  }, []);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    refreshSummary();

    let alive = true;
    const connect = () => {
      if (!alive) return;
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        listeners.current.forEach((fn) => fn(msg));
        let n = null;
        let cat = "email";
        if (msg.type === "new_message") {
          cat = "chat";
          n = [`💬 ${msg.from}`, msg.message?.type === "text" ? msg.message.text : "Nouveau message"];
        } else if (msg.type === "new_email") {
          cat = "email";
          n = [`📧 ${msg.from}`, msg.subject];
        } else if (msg.type === "task_validated") {
          cat = "validation";
          n = [msg.decision === "validated" ? "✅ Tâche validée" : "❌ Tâche refusée", msg.title];
        } else if (msg.type === "widget_ticket") {
          cat = "ticket";
          n = ["🎫 Nouveau ticket (widget)", `${msg.from} · ${msg.subject}`];
        } else if (msg.type === "widget_reply") {
          cat = "ticket";
          n = ["💬 Réponse client (widget)", msg.subject];
        } else if (msg.type === "ticket_reply" || msg.type === "ticket_converted") {
          cat = "ticket";
          n = [msg.type === "ticket_converted" ? "🔁 Ticket converti en tâche" : "💬 Réponse à votre ticket", msg.subject || msg.title];
        }
        if (n) {
          toast(n[0], { description: n[1] });
          fireNotification(n[0], n[1], cat);
        }
        setEvents((e) => e + 1);
        refreshSummary();
      };
      ws.onclose = () => { if (alive) setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    const poll = setInterval(refreshSummary, 20000);
    return () => {
      alive = false;
      clearInterval(poll);
      try { wsRef.current?.close(); } catch {}
    };
  }, [user, refreshSummary]);

  return (
    <RealtimeContext.Provider value={{ summary, perCompany, refreshSummary, subscribe, events }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
